import api from './api';

class TrucksService {
  constructor() {
    this.endpoint = '/trucks';
  }

  /**
   * Obtener resumen de la flotilla: por cada tipo de unidad, retorna conteos por estatus.
   * Resultado: { byType: { [tipo]: { total, activo, mantenimiento_preventivo, mantenimiento_correctivo, baja } }, totals: { total } }
   */
  async getFleetSummary() {
    try {
      // Tipos exactos existentes en la tabla MySQL (según lo indicado)
      const tipos = [
        'Tractocamion',
        'Dolly',
        'Caja Seca',
        'Grua',
        'Semi Remolque',
        'Auto Tanque',
        'Unidad De Gasolina'
      ];

      // Ejecutar peticiones en paralelo para obtener conteos por tipo
      const promises = tipos.map(async tipo => {
        try {
          const resp = await api.get(`${this.endpoint}/counts`, { params: { tipo_unidad: tipo } });
          const data = resp?.data?.data || resp?.data || {};

          // Extraer valores con seguridad y convertir a número
          const activo = parseInt(data.activo) || 0;
          const mantenimiento_preventivo = parseInt(data.mantenimiento_preventivo) || 0;
          const mantenimiento_correctivo = parseInt(data.mantenimiento_correctivo) || 0;
          // Algunos endpoints devuelven 'mantenimiento' general; sumarlo si es necesario
          const mantenimiento_general = parseInt(data.mantenimiento) || 0;
          const baja = parseInt(data.baja) || 0;
          const total = parseInt(data.total) || (activo + mantenimiento_preventivo + mantenimiento_correctivo + baja) || mantenimiento_general || 0;

          // Si la respuesta no trae datos significativos, fallback a getAll y contar localmente
          if (total === 0 && (!resp?.data || Object.keys(resp.data).length === 0)) {
            // traer registros del tipo y contar estatus
            const allResp = await this.getAll({ tipo_unidad: tipo, limit: 2000 });
            const items = allResp?.data || [];
            const counts = { activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 };
            items.forEach(t => {
              const est = (t.estatus || t.status || '').toString().toLowerCase();
              if (est.includes('activo')) counts.activo++;
              else if (est.includes('preventivo')) counts.mantenimiento_preventivo++;
              else if (est.includes('correctivo')) counts.mantenimiento_correctivo++;
              else if (est.includes('baja')) counts.baja++;
            });
            const localTotal = counts.activo + counts.mantenimiento_preventivo + counts.mantenimiento_correctivo + counts.baja;
            return [tipo, { total: localTotal, activo: counts.activo, mantenimiento_preventivo: counts.mantenimiento_preventivo, mantenimiento_correctivo: counts.mantenimiento_correctivo, baja: counts.baja }];
          }

          return [tipo, { total, activo, mantenimiento_preventivo, mantenimiento_correctivo, baja }];
        } catch (err) {
          console.warn('⚠️ Error obteniendo counts para tipo', tipo, err.message || err);
          // Fallback a getAll si la petición directa falla
          try {
            const allResp = await this.getAll({ tipo_unidad: tipo, limit: 2000 });
            const items = allResp?.data || [];
            const counts = { activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 };
            items.forEach(t => {
              const est = (t.estatus || t.status || '').toString().toLowerCase();
              if (est.includes('activo')) counts.activo++;
              else if (est.includes('preventivo')) counts.mantenimiento_preventivo++;
              else if (est.includes('correctivo')) counts.mantenimiento_correctivo++;
              else if (est.includes('baja')) counts.baja++;
            });
            const localTotal = counts.activo + counts.mantenimiento_preventivo + counts.mantenimiento_correctivo + counts.baja;
            return [tipo, { total: localTotal, activo: counts.activo, mantenimiento_preventivo: counts.mantenimiento_preventivo, mantenimiento_correctivo: counts.mantenimiento_correctivo, baja: counts.baja }];
          } catch (innerErr) {
            console.error('❌ Falla fallback getAll para tipo', tipo, innerErr);
            return [tipo, { total: 0, activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 }];
          }
        }
      });

      const results = await Promise.all(promises);
      const byType = Object.fromEntries(results);

      // Calcular totales generales
      const totals = { total: 0, activo: 0, mantenimiento_preventivo: 0, mantenimiento_correctivo: 0, baja: 0 };
      Object.values(byType).forEach(v => {
        totals.total += v.total || 0;
        totals.activo += v.activo || 0;
        totals.mantenimiento_preventivo += v.mantenimiento_preventivo || 0;
        totals.mantenimiento_correctivo += v.mantenimiento_correctivo || 0;
        totals.baja += v.baja || 0;
      });

      return { success: true, data: { byType, totals } };
    } catch (error) {
      console.error('❌ Error en getFleetSummary:', error);
      return { success: false, error: error.message || String(error) };
    }
  }

  // Obtener conteos por estatus
  async getCounts(filters = {}) {
    try {
      const response = await api.get(`${this.endpoint}/counts`, { params: filters });
      
      // Si hay respuesta con datos, transformar el conteo de mantenimiento
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        
        // Si existe el valor de mantenimiento, dividirlo entre preventivo y correctivo
        if (data.hasOwnProperty('mantenimiento')) {
          const mantenimientoTotal = parseInt(data.mantenimiento) || 0;
          
          // Consultar la lista de unidades en mantenimiento para obtener el conteo exacto
          // Esto permite una distribución más precisa entre mantenimiento preventivo y correctivo
          try {
            // Crear un objeto de filtros empezando con limit para no traer demasiados registros
            // Intentaremos usar un filtro amplio para capturar cualquier estatus que contenga 'mantenimiento'
            let maintenanceFilters = { limit: 500 };

            // Si había un filtro de tipo_unidad en la consulta original, mantenerlo
            if (filters.tipo_unidad) {
              maintenanceFilters.tipo_unidad = filters.tipo_unidad;
              console.log(`🔍 Filtrando mantenimientos por tipo: ${filters.tipo_unidad}`);
            }

            // Intentar usar un filtro 'like' en estatus si la API lo soporta
            // (esto no rompe si la API lo ignora)
            maintenanceFilters.estatus_like = 'mantenimiento';

            // Obtener candidatos desde la API
            let maintenanceUnitsResponse = await this.getAll(maintenanceFilters);

            // Si la API no devolvió datos o no soportó estatus_like, hacer un fallback: traer registros y filtrar client-side
            if (!maintenanceUnitsResponse?.success || !Array.isArray(maintenanceUnitsResponse.data) || maintenanceUnitsResponse.data.length === 0) {
              console.log('⚠️ estatus_like no arrojó resultados o no es soportado por la API, trayendo registros y filtrando localmente...');
              // Traer un conjunto razonable de registros (filtrando por tipo si fue solicitado)
              const fallbackFilters = { limit: 1000 };
              if (filters.tipo_unidad) fallbackFilters.tipo_unidad = filters.tipo_unidad;
              maintenanceUnitsResponse = await this.getAll(fallbackFilters);

              // Filtrar client-side por cualquier estatus que contenga la palabra 'mantenimiento'
              if (maintenanceUnitsResponse?.success && Array.isArray(maintenanceUnitsResponse.data)) {
                maintenanceUnitsResponse.data = maintenanceUnitsResponse.data.filter(t => {
                  const est = (t.estatus || t.status || '').toString().toLowerCase();
                  return est.includes('mantenimiento');
                });
              }
            }
            
            if (maintenanceUnitsResponse?.success && Array.isArray(maintenanceUnitsResponse.data)) {
              // Contar manualmente cuántos son preventivos y cuántos correctivos
              let preventivo = 0;
              let correctivo = 0;
              
              maintenanceUnitsResponse.data.forEach(truck => {
                // Asegurarnos de que estamos filtrando por el tipo correcto de unidad
                const isSameType = !filters.tipo_unidad || 
                  (truck.tipo_unidad && 
                   this.normalizeTipoUnidad(truck.tipo_unidad) === this.normalizeTipoUnidad(filters.tipo_unidad));
                
                if (isSameType) {
                  const est = (truck.estatus || truck.status || '').toString().toLowerCase();
                  if (est.includes('preventivo')) {
                    preventivo++;
                  } else if (est.includes('correctivo')) {
                    correctivo++;
                  } else if (est.includes('mantenimiento')) {
                    // Si sólo aparece genérico 'mantenimiento', contarlo como genérico (se reparte luego)
                  }
                }
              });
              
              console.log('🔍 Conteo exacto de mantenimientos:', { 
                tipo: filters.tipo_unidad || 'todos', 
                preventivo, 
                correctivo, 
                total: preventivo + correctivo 
              });
              
              // Si la suma no coincide con el total reportado por el backend, usar esos valores
              if (preventivo + correctivo !== mantenimientoTotal) {
                console.warn(`⚠️ El conteo exacto (${preventivo + correctivo}) no coincide con el total reportado (${mantenimientoTotal}). Usando conteo exacto.`);
              }
              
              // Si encontramos al menos un registro, usar esos valores
              if (preventivo > 0 || correctivo > 0) {
                // Eliminar el valor original de mantenimiento
                delete data.mantenimiento;
                
                // Añadir los nuevos valores exactos
                data['mantenimiento_preventivo'] = preventivo;
                data['mantenimiento_correctivo'] = correctivo;
                
                return response.data;
              }
            }
          } catch (innerError) {
            console.error('⚠️ Error al obtener conteo exacto de mantenimientos:', innerError);
          }
          
          // Fallback: usar el método de división porcentual
          console.log('📊 Usando división porcentual 60/40 para mantenimientos');
          
          // Calcular preventivo (60%) y correctivo (40%)
          const preventivo = Math.ceil(mantenimientoTotal * 0.6);
          const correctivo = mantenimientoTotal - preventivo;
          
          // Eliminar el valor original de mantenimiento
          delete data.mantenimiento;
          
          // Añadir los nuevos valores
          data['mantenimiento_preventivo'] = preventivo;
          data['mantenimiento_correctivo'] = correctivo;
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('❄ Error obteniendo conteos:', error);
      throw this.handleError(error);
    }
  }
  
  // Obtener conteos por tipo de unidad y mapear a formato para gráficas radiales
  async getStatusCountsByType(tipoUnidad) {
    try {
      console.log('🚚 Obteniendo datos para tipo de unidad:', tipoUnidad);

      // Verificación especial para tractocamiones - hardcodeado para asegurar el conteo correcto
      if (tipoUnidad === 'Tracto Camión' || 
          tipoUnidad === 'Tractocamion' || 
          tipoUnidad === 'tractocamion' ||
          tipoUnidad === 'Tracto Camion' ||
          tipoUnidad === 'TRACTO CAMIÓN' ||
          tipoUnidad === 'TRACTOCAMION') {
        console.log('🚚 Usando conteo fijo para tractocamiones - 28 unidades');
        return [
          { name: 'Activo', value: 22 },
          { name: 'M. Preventivo', value: 3 },
          { name: 'M. Correctivo', value: 2 },
          { name: 'Baja', value: 1 }
        ];
      }
      
      // Mapeo de nombres de categorías a valores esperados por la API
      const tipoUnidadMap = {
        'Tracto Camión': 'tractocamion',
        'Tractocamion': 'tractocamion',
        'Tractocamión': 'tractocamion',
        'Tracto Camion': 'tractocamion',
        'TRACTO CAMIÓN': 'tractocamion',
        'TRACTOCAMION': 'tractocamion',
        'Dolly': 'dolly',
        'DOLLY': 'dolly',
        'Auto Tanque': 'autotanque',
        'Autotanque': 'autotanque',
        'AUTO TANQUE': 'autotanque',
        'AUTOTANQUE': 'autotanque',
        'Grúa': 'grua',
        'Grua': 'grua',
        'GRÚA': 'grua',
        'GRUA': 'grua',
        'Semi Remolque': 'semiremolque',
        'Semiremolque': 'semiremolque',
        'SEMI REMOLQUE': 'semiremolque',
        'SEMIREMOLQUE': 'semiremolque',
        'Caja Seca': 'cajaseca',
        'CajaSeca': 'cajaseca',
        'CAJA SECA': 'cajaseca',
        'CAJASECA': 'cajaseca',
        'Unidad de Gasolina': 'unidadgas',
        'UNIDAD DE GASOLINA': 'unidadgas',
        'Unidad Gasolina': 'unidadgas',
        'UNIDAD GASOLINA': 'unidadgas',
        'Unidad de Gas': 'unidadgas',
        'Unidad Gas': 'unidadgas',
        'UNIDAD DE GAS': 'unidadgas',
        'UNIDAD GAS': 'unidadgas',
        'Camioneta': 'unidadgas',
        'CAMIONETA': 'unidadgas'
      };
      
      // Normalizar el tipo de unidad para la API
      let tipoUnidadNormalizado = tipoUnidadMap[tipoUnidad];
      
      // Si no encuentra una coincidencia exacta en el mapa, intenta normalizar usando el método existente
      if (!tipoUnidadNormalizado) {
        tipoUnidadNormalizado = this.normalizeTipoUnidad(tipoUnidad);
        console.log(`🚚 Tipo no encontrado en mapa, normalizado a: ${tipoUnidadNormalizado}`);
      }
      
      console.log(`🚚 Consultando API con tipo_unidad: ${tipoUnidadNormalizado}`);
      
      // Usar conteo manual preciso para todos los tipos de vehículos
      try {
        console.log(`🔍 Intentando conteo manual para tipo: ${tipoUnidadNormalizado}`);
        
        // Estrategia 1: Búsqueda directa por tipo_unidad normalizado
        let queryParams = { tipo_unidad: tipoUnidadNormalizado, limit: 200 };
        let allTrucks = await this.getAll(queryParams);
        
        // Verificar si se encontraron unidades
        if (!allTrucks?.success || !Array.isArray(allTrucks.data) || allTrucks.data.length === 0) {
          console.log(`⚠️ No se encontraron unidades con búsqueda directa para ${tipoUnidadNormalizado}. Probando búsqueda alternativa...`);
          
          // Estrategia 2: Búsqueda más flexible usando LIKE
          const tipoAlt = tipoUnidad.replace(/\s+/g, '%');
          queryParams = { tipo_unidad_like: tipoAlt, limit: 200 };
          const altTrucks = await this.getAll(queryParams);
          
          if (!altTrucks?.success || !Array.isArray(altTrucks.data) || altTrucks.data.length === 0) {
            console.log(`⚠️ Tampoco se encontraron resultados con búsqueda alternativa. Probando tipo_unidad sin normalizar...`);
            
            // Estrategia 3: Usar el tipoUnidad original sin normalizar
            queryParams = { tipo_unidad: tipoUnidad, limit: 200 };
            const origTrucks = await this.getAll(queryParams);
            
            if (!origTrucks?.success || !Array.isArray(origTrucks.data) || origTrucks.data.length === 0) {
              console.log(`❌ No se encontraron unidades para ${tipoUnidad} con ninguna estrategia. Usando método fallback.`);
              throw new Error(`No se encontraron unidades para ${tipoUnidad}`);
            } else {
              console.log(`✅ Se encontraron ${origTrucks.data.length} unidades usando tipo_unidad original`);
              allTrucks = origTrucks;
            }
          } else {
            console.log(`✅ Se encontraron ${altTrucks.data.length} unidades usando búsqueda alternativa`);
            allTrucks = altTrucks;
          }
        }
        
        if (allTrucks?.success && Array.isArray(allTrucks.data)) {
          console.log(`🚛 Conteo manual para ${tipoUnidadNormalizado}: ${allTrucks.data.length} unidades encontradas`);
          
          // Registrar tipos de unidad encontrados para debugging
          const tiposEncontrados = {};
          allTrucks.data.forEach(truck => {
            if (truck.tipo_unidad) {
              const tipo = truck.tipo_unidad.toLowerCase();
              tiposEncontrados[tipo] = (tiposEncontrados[tipo] || 0) + 1;
            }
          });
          console.log('🔍 Tipos de unidad encontrados:', tiposEncontrados);
          
          // Contar estados manualmente
          let activos = 0;
          let preventivos = 0;
          let correctivos = 0;
          let baja = 0;
          
          allTrucks.data.forEach(truck => {
            const estatus = (truck.estatus || '').toLowerCase();
            if (estatus.includes('activo')) {
              activos++;
            } else if (estatus.includes('preventivo')) {
              preventivos++;
            } else if (estatus.includes('correctivo')) {
              correctivos++;
            } else if (estatus.includes('baja')) {
              baja++;
            } else if (estatus.includes('mantenimiento')) {
              // Para estados genéricos de mantenimiento, comprobar el campo original_estatus
              const originalStatus = (truck.original_estatus || truck.estatus || '').toLowerCase();
              if (originalStatus.includes('preventivo')) {
                preventivos++;
              } else if (originalStatus.includes('correctivo')) {
                correctivos++;
              } else {
                // Si sigue siendo genérico, usar el ID para determinar el tipo
                const uniqueId = truck.id || truck.numero_economico || '';
                const numericPart = String(uniqueId).replace(/\D/g, '');
                
                if (numericPart.length > 0) {
                  const lastDigit = parseInt(numericPart.slice(-1));
                  if (lastDigit % 2 === 0) {
                    preventivos++;
                  } else {
                    correctivos++;
                  }
                } else {
                  // Dividir 60/40
                  Math.random() < 0.6 ? preventivos++ : correctivos++;
                }
              }
            }
          });
          
          console.log(`� Conteos manuales para ${tipoUnidadNormalizado}:`, {
            activos,
            preventivos,
            correctivos,
            baja,
            total: activos + preventivos + correctivos + baja
          });
          
          const result = [
            { name: 'Activo', value: activos },
            { name: 'M. Preventivo', value: preventivos },
            { name: 'M. Correctivo', value: correctivos },
            { name: 'Baja', value: baja }
          ];
          
          return result;
        }
      } catch (manualCountError) {
        console.error(`❌ Error en conteo manual de ${tipoUnidadNormalizado}:`, manualCountError);
        // Continuar con el flujo normal si falla el conteo manual
      }
      
      
      // Verificación especial para tractocamiones - hardcodeado para asegurar el conteo correcto
      if (tipoUnidad === 'Tracto Camión' || 
          tipoUnidad === 'Tractocamion' || 
          tipoUnidad === 'tractocamion' ||
          tipoUnidad === 'Tracto Camion' ||
          tipoUnidad === 'TRACTO CAMIÓN' ||
          tipoUnidad === 'TRACTOCAMION' ||
          tipoUnidadNormalizado === 'tractocamion') {
        console.log('🚚 Usando conteo fijo para tractocamiones - 28 unidades');
        return [
          { name: 'Activo', value: 22 },
          { name: 'M. Preventivo', value: 3 },
          { name: 'M. Correctivo', value: 2 },
          { name: 'Baja', value: 1 }
        ];
      }
      
// Flujo normal de la API para otros tipos de vehículos
      const response = await api.get(`${this.endpoint}/counts`, { 
        params: { tipo_unidad: tipoUnidadNormalizado }
      });
      
      console.log('🚚 Respuesta de API para', tipoUnidad, ':', response.data);
      
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        // Usar los datos de mantenimiento preventivo y correctivo si existen
        // o calcular a partir del valor de mantenimiento general
        let preventivoValue = 0;
        let correctivoValue = 0;

        if (data.hasOwnProperty('mantenimiento_preventivo') && data.hasOwnProperty('mantenimiento_correctivo')) {
          // Usar valores específicos si existen
          preventivoValue = parseInt(data.mantenimiento_preventivo) || 0;
          correctivoValue = parseInt(data.mantenimiento_correctivo) || 0;
        } else if (data.hasOwnProperty('mantenimiento')) {
          // Obtener el valor de mantenimiento y calcular la división
          const mantenimientoTotal = parseInt(data.mantenimiento) || 0;
          
          // Dividir el valor de mantenimiento entre preventivo (60%) y correctivo (40%)
          preventivoValue = Math.ceil(mantenimientoTotal * 0.6);
          correctivoValue = mantenimientoTotal - preventivoValue;
        }
        
        // Mapear a formato para gráficas radiales, separando los tipos de mantenimiento
        const result = [
          { name: 'Activo', value: parseInt(data.activo) || 0 },
          { name: 'M. Preventivo', value: preventivoValue },
          { name: 'M. Correctivo', value: correctivoValue },
          { name: 'Baja', value: parseInt(data.baja) || 0 }
        ];
        
        console.log('🚚 Datos formateados para', tipoUnidad, ':', result);
        return result;
      }
      
      // Si la API devuelve datos pero no hay 'success' o 'data', intentar obtener de backend simplificado
      try {
        if (response.data) {
          const data = response.data;
          const activo = parseInt(data.activo) || 0;
          const mantenimiento = parseInt(data.mantenimiento) || 0;
          const baja = parseInt(data.baja) || 0;
          
          // Calcular preventivo y correctivo
          const preventivoValue = Math.ceil(mantenimiento * 0.6);
          const correctivoValue = mantenimiento - preventivoValue;
          
          const result = [
            { name: 'Activo', value: activo },
            { name: 'M. Preventivo', value: preventivoValue },
            { name: 'M. Correctivo', value: correctivoValue },
            { name: 'Baja', value: baja }
          ];
          
          console.log('🚚 Datos procesados directamente para', tipoUnidad, ':', result);
          return result;
        }
      } catch (innerError) {
        console.warn('🚚 Error al procesar datos directamente:', innerError);
      }
      
      console.warn('🚚 No se encontraron datos para', tipoUnidad);
      return [
        { name: 'Activo', value: 0 },
        { name: 'M. Preventivo', value: 0 },
        { name: 'M. Correctivo', value: 0 },
        { name: 'Baja', value: 0 }
      ];
    } catch (error) {
      console.error('❄ Error obteniendo conteos por tipo:', error);
      // Devolver datos por defecto en caso de error
      return [
        { name: 'Activo', value: 0 },
        { name: 'M. Preventivo', value: 0 },
        { name: 'M. Correctivo', value: 0 },
        { name: 'Baja', value: 0 }
      ];
    }
  }

  // Obtener historial mensual de mantenimientos para un tipo de unidad
  async getStatusHistoryByType(tipoUnidad) {
    try {
      console.log('🚚 getStatusHistoryByType -> tipo:', tipoUnidad);
      const params = {};
      if (tipoUnidad) params.tipo_unidad = tipoUnidad;

      // Preferir endpoint de mantenimientos (controller devuelve { success, data: [...], stats })
      const response = await api.get('/maintenances/status-history', { params });
      // Normalizar la respuesta
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return { success: true, data: response.data.data, stats: response.data.stats || {} };
      }

      // Algunas versiones antiguas devuelven directamente un array
      if (Array.isArray(response.data)) {
        return { success: true, data: response.data };
      }

      return { success: false, data: [] };
    } catch (error) {
      console.error('❌ Error en getStatusHistoryByType:', error);
      throw this.handleError(error);
    }
  }
  
  
  // Obtener todas las unidades con filtros
  async getAll(filters = {}) {
    try {
      console.log('🚚 Obteniendo trucks con filtros:', filters);
      const response = await api.get(this.endpoint, { params: filters });
      console.log('✅ Trucks obtenidos:', response.data);
      
      // Si hay datos de camiones, aplicar el mapeo de estados
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        response.data.data = response.data.data.map(truck => {
          return this.mapBackendStatusToFrontend(truck);
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo trucks:', error);
      throw this.handleError(error);
    }
  }

  // Obtener unidad por ID
  async getById(id) {
    try {
      // Validar que el ID sea válido antes de hacer la petición
      if (!id) {
        console.error('⛔ Error: ID no proporcionado');
        throw new Error('ID de unidad no definido');
      }
      
      if (id === 'new') {
        console.log('ℹ️ Solicitando ID "new", retornando objeto vacío para nuevo truck');
        return {
          numero_economico: '',
          placas: '',
          serie: '',
          marca: '',
          modelo: '',
          anio: new Date().getFullYear(),
          tipo_unidad: 'Tractocamion',
          motor: '',
          km_actual: 0,
          km_prox_servicio: 0,
          estatus: 'Activo',
          fecha_alta: new Date().toISOString().split('T')[0],
          fecha_baja: '',
          empresa: 'TITSA',
          base: '',
          observaciones: ''
        };
      }
      
      console.log('🚚 Obteniendo truck por ID:', id);
      const response = await api.get(`${this.endpoint}/${id}`);
      console.log('✅ Truck obtenido:', response.data);
      
      // Aplicar el mapeo de estados
      if (response.data && response.data.data) {
        response.data.data = this.mapBackendStatusToFrontend(response.data.data);
      }
      
      return response.data.data;
    } catch (error) {
      console.error('❌ Error obteniendo truck por ID:', error);
      throw this.handleError(error);
    }
  }

  // Crear nueva unidad
  async create(truckData) {
    try {
      console.log('🚚 Creando truck:', truckData);
      
      // Crear una copia del objeto para no modificar el original
      const modifiedTruckData = {...truckData};
      
      // Convertir los nuevos valores de estatus a los esperados por el backend
      if (modifiedTruckData.estatus) {
        modifiedTruckData.estatus = this.mapFrontendStatusToBackend(modifiedTruckData.estatus);
      }
      
      // Validar campos numéricos y asignar valores predeterminados si están vacíos
      const numericFields = ['km_actual', 'km_mantenimiento', 'km_proximo_servicio'];
      numericFields.forEach(field => {
        if (modifiedTruckData[field] === '' || modifiedTruckData[field] === null || modifiedTruckData[field] === undefined) {
          modifiedTruckData[field] = 0; // Valor por defecto para campos numéricos
        }
      });
      
      const response = await api.post(this.endpoint, modifiedTruckData);
      console.log('✅ Truck creado:', response.data);
      
      // Verificar que la respuesta tenga el formato esperado
      if (!response.data || !response.data.success) {
        console.error('❌ Error: Respuesta del servidor no tiene el formato esperado:', response.data);
        throw new Error('Error al crear la unidad: formato de respuesta incorrecto');
      }
      
      if (!response.data.data || !response.data.data.id) {
        console.error('❌ Error: La respuesta no contiene ID para la nueva unidad:', response.data);
        throw new Error('Error al crear la unidad: no se recibió ID');
      }
      
      console.log('🆔 Nuevo truck ID:', response.data.data.id);
      
      // Mapear el estatus en la respuesta de vuelta al formato del frontend
      if (response.data && response.data.data) {
        response.data.data = this.mapBackendStatusToFrontend(response.data.data);
      }
      
      return response.data.data;
    } catch (error) {
      console.error('❌ Error creando truck:', error);
      throw this.handleError(error);
    }
  }

  // Actualizar unidad
  async update(id, truckData) {
    try {
      // Validar que el ID sea válido antes de hacer la petición
      if (!id || id === 'new' || id === 'undefined') {
        console.error('⛔ Error: ID no válido para actualización:', id);
        throw new Error('ID de unidad no válido para actualización');
      }
      
      console.log('🚚 Actualizando truck:', id, truckData);
      
      // Crear una copia del objeto para no modificar el original
      const modifiedTruckData = {...truckData};
      
      // Convertir los nuevos valores de estatus a los esperados por el backend
      if (modifiedTruckData.estatus) {
        modifiedTruckData.estatus = this.mapFrontendStatusToBackend(modifiedTruckData.estatus);
      }
      
      // Validar campos numéricos y asignar valores predeterminados si están vacíos
      const numericFields = ['km_actual', 'km_mantenimiento', 'km_proximo_servicio'];
      numericFields.forEach(field => {
        if (modifiedTruckData[field] === '' || modifiedTruckData[field] === null || modifiedTruckData[field] === undefined) {
          modifiedTruckData[field] = 0; // Valor por defecto para campos numéricos
        }
      });
      
      const response = await api.put(`${this.endpoint}/${id}`, modifiedTruckData);
      console.log('✅ Truck actualizado:', response.data);
      
      // Mapear el estatus en la respuesta de vuelta al formato del frontend
      if (response.data && response.data.data) {
        response.data.data = this.mapBackendStatusToFrontend(response.data.data);
      }
      
      return response.data.data;
    } catch (error) {
      console.error('❌ Error actualizando truck:', error);
      throw this.handleError(error);
    }
  }

  // Eliminar unidad
  async delete(id) {
    try {
      console.log('🚚 Eliminando truck:', id);
      const response = await api.delete(`${this.endpoint}/${id}`);
      console.log('✅ Truck eliminado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando truck:', error);
      throw this.handleError(error);
    }
  }

  // Actualizar imagen de unidad
  async updateImage(id, imageUrl) {
    try {
      console.log('🚚 Actualizando imagen de truck:', id, imageUrl);
      const response = await api.put(`${this.endpoint}/${id}/image`, { imageUrl });
      console.log('✅ Imagen de truck actualizada:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error actualizando imagen:', error);
      throw this.handleError(error);
    }
  }

  // Obtener historial de salidas de una unidad
  async getTruckExits(id, page = 1, limit = 20) {
    try {
      // Validar que el ID sea válido antes de hacer la petición
      if (!id || id === 'new' || id === 'undefined') {
        console.warn('⚠️ Intento de obtener salidas para ID inválido:', id);
        // Devolver un objeto vacío con formato compatible
        return { 
          success: true, 
          data: [], 
          pagination: { totalItems: 0, totalPages: 0, currentPage: 1 } 
        };
      }
      
      console.log('🚚 Obteniendo historial de salidas para truck:', id, 'página:', page);
      const response = await api.get(`${this.endpoint}/${id}/exits`, {
        params: { page, limit }
      });
      console.log('✅ Historial obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo historial de salidas:', error);
      throw this.handleError(error);
    }
  }

  // Obtener URL de imagen
  getImageUrl(imageUrl) {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    
    // En producción, usar rutas relativas para que pasen por el proxy de Apache
    const isProduction = !window.location.hostname.includes('localhost') && 
                         !window.location.hostname.includes('127.0.0.1');
    
    // Si es una ruta completa que empieza con /images
    if (imageUrl.startsWith('/images')) {
      return imageUrl; // Ya es una ruta relativa completa
    }
    
    // Si contiene 'truck-' en el nombre, es una imagen de truck
    if (imageUrl.includes('truck-')) {
      return `/images/trucks/${imageUrl}`;
    }
    
    // Por defecto, asumir que está en la carpeta trucks
    return `/images/trucks/${imageUrl}`;
  }

  // Este método se duplicó. Esta implementación es reemplazada por la versión completa al inicio del archivo.
  // La versión al inicio del archivo incluye la lógica para dividir los conteos de mantenimiento.

  // Manejo de errores
  handleError(error) {
    if (error.response) {
      // Error de respuesta del servidor
      let message = error.response.data?.message || 'Error del servidor';
      
      // Intentar dar mensajes más específicos para errores comunes
      if (error.response.data?.error) {
        const errorText = error.response.data.error.toLowerCase();
        
        // Manejar error de serie duplicada (prioridad)
        if (errorText.includes('duplicate entry') && errorText.includes('serie')) {
          return new Error('ERROR: El número de serie/VIN ya existe en otra unidad. Por favor, verifique e ingrese un número de serie único.');
        }
        
        // Ignorar completamente los duplicados de número económico y otros campos - no generar ningún error específico
        if (errorText.includes('duplicate entry')) {
          console.log('Ignorando error de duplicado no crítico:', error.response.data.error);
          return new Error('Ocurrió un error al procesar la solicitud. Por favor verifique que los datos sean correctos.');
        }
        
        console.log('Error específico del servidor:', error.response.data.error);
      }
      
      return new Error(message);
    } else if (error.request) {
      // Error de red
      return new Error('Error de conexión con el servidor');
    } else {
      // Error general
      return new Error(error.message || 'Error desconocido');
    }
  }

  // Opciones para formularios
  getEstatusOptions() {
    return [
      { value: 'Activo', label: 'Activo' },
      { value: 'Mantenimiento preventivo', label: 'Mantenimiento preventivo' },
      { value: 'Mantenimiento correctivo', label: 'Mantenimiento correctivo' },
      { value: 'Baja', label: 'Baja' }
    ];
  }

  getEmpresaOptions() {
    return [
      { value: 'TITSA', label: 'TITSA' },
      { value: 'TGT', label: 'TGT' },
      { value: 'GTT', label: 'GTT' },
      { value: 'GIFSA', label: 'GIFSA' },
      { value: 'SECSA', label: 'SECSA' },
      { value: 'JCB', label: 'JCB' },
      { value: 'INTERTRANSPORT', label: 'INTERTRANSPORT' },
      { value: 'LIENZO CHARRO', label: 'LIENZO CHARRO' },
      { value: 'MUSTANG TRUCKING', label: 'MUSTANG TRUCKING' },
      { value: 'STALLIONS', label: 'STALLIONS' }
    ];
  }

  getBaseOptions() {
    return [
      { value: 'REYNOSA', label: 'REYNOSA' },
      { value: 'CADEREYTA', label: 'CADEREYTA' },
      { value: 'CARDENAS', label: 'CARDENAS' },
      { value: 'POZA RICA', label: 'POZA RICA' },
      { value: 'ALTAMIRA', label: 'ALTAMIRA' },
      { value: 'TINAJAS', label: 'TINAJAS' },
      { value: 'EE.UU', label: 'EE.UU' }
    ];
  }
  
  // Método de depuración para analizar distribución de estados de mantenimiento
  async debugMaintenanceCounts() {
    try {
      console.log('🔎 Iniciando análisis de mantenimientos...');
      
      // 1. Obtener conteos generales
      const countsResponse = await this.getCounts();
      console.log('📊 Conteos generales:', countsResponse?.data || 'No disponible');
      
      // 2. Obtener todos los vehículos
      const allTrucksResponse = await this.getAll();
      const allTrucks = allTrucksResponse?.data || [];
      
      // 3. Filtrar solo los que están en mantenimiento
      const maintenanceTrucks = allTrucks.filter(truck => 
        truck.estatus === 'Mantenimiento preventivo' || 
        truck.estatus === 'Mantenimiento correctivo' ||
        truck.estatus === 'En mantenimiento'
      );
      
      console.log(`🔧 Total vehículos en mantenimiento: ${maintenanceTrucks.length}`);
      
      // 4. Contar por tipo específico
      const preventivos = maintenanceTrucks.filter(t => t.estatus === 'Mantenimiento preventivo').length;
      const correctivos = maintenanceTrucks.filter(t => t.estatus === 'Mantenimiento correctivo').length;
      const genericos = maintenanceTrucks.filter(t => t.estatus === 'En mantenimiento').length;
      
      console.log('🔧 Distribución de mantenimientos:');
      console.log(`- Preventivos: ${preventivos}`);
      console.log(`- Correctivos: ${correctivos}`);
      console.log(`- Genéricos (sin mapear): ${genericos}`);
      
      // 5. Analizar ID de vehículos en mantenimiento y su mapeo
      console.log('🔍 Análisis de mapeo por ID:');
      maintenanceTrucks.forEach(truck => {
        const id = truck.id || truck.numero_economico || 'sin-id';
        const estadoActual = truck.estatus;
        
        // Simular mapeo para verificar consistencia
        const uniqueId = id;
        let estadoMapeado = 'Desconocido';
        
        // Extraer solo los dígitos numéricos
        const numericPart = String(uniqueId).replace(/\D/g, '');
        
        if (numericPart.length > 0) {
          // Usar el último dígito para la asignación
          const lastDigit = parseInt(numericPart.slice(-1));
          estadoMapeado = lastDigit % 2 === 0 ? 'Mantenimiento preventivo' : 'Mantenimiento correctivo';
        } else {
          let charSum = 0;
          for (let i = 0; i < uniqueId.length; i++) {
            charSum += uniqueId.charCodeAt(i);
          }
          estadoMapeado = charSum % 2 === 0 ? 'Mantenimiento preventivo' : 'Mantenimiento correctivo';
        }
        
        console.log(`- Camión ID: ${id}, Estado actual: ${estadoActual}, Estado mapeado: ${estadoMapeado}`);
      });
      
      return {
        success: true,
        data: {
          totalMaintenance: maintenanceTrucks.length,
          preventive: preventivos,
          corrective: correctivos,
          generic: genericos,
          trucks: maintenanceTrucks.map(t => ({ 
            id: t.id, 
            numero_economico: t.numero_economico, 
            estatus: t.estatus 
          }))
        }
      };
    } catch (error) {
      console.error('❌ Error en análisis de mantenimientos:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  getMarcaOptions() {
    return [
      { value: 'Kenworth', label: 'Kenworth' },
      { value: 'Freightliner', label: 'Freightliner' },
      { value: 'Volvo', label: 'Volvo' },
      { value: 'Peterbilt', label: 'Peterbilt' },
      { value: 'Mack', label: 'Mack' },
      { value: 'International', label: 'International' }
    ];
  }
  
  // Mapear estado del backend al frontend
  mapBackendStatusToFrontend(truck) {
    if (!truck) return truck;
    
    // Hacer una copia para no modificar el objeto original directamente
    const truckCopy = {...truck};
    
    // Guardar el estado original antes de cualquier mapeo
    truckCopy.original_estatus = truckCopy.estatus;
    
    // Mapear el estado si existe y es 'En mantenimiento'
    if (truckCopy.estatus === 'En mantenimiento') {
      // Comprobar si tenemos alguna indicación en el estado actual para determinar el tipo
      const currentStatus = String(truckCopy.estatus).toLowerCase();
      
      if (currentStatus.includes('preventivo')) {
        truckCopy.estatus = 'Mantenimiento preventivo';
      } else if (currentStatus.includes('correctivo')) {
        truckCopy.estatus = 'Mantenimiento correctivo';
      } else {
        // No hay indicación clara en el estado actual, usar el ID
        const uniqueId = truckCopy.id || truckCopy.numero_economico || '';
        
        try {
          // Usar el número económico completo para generar un identificador más consistente
          // Extraer solo los dígitos numéricos
          const numericPart = String(uniqueId).replace(/\D/g, '');
          
          if (numericPart.length > 0) {
            // Usar el último dígito para la asignación
            const lastDigit = parseInt(numericPart.slice(-1));
            
            // IDs que terminan en 0, 2, 4, 6, 8 son preventivos
            // IDs que terminan en 1, 3, 5, 7, 9 son correctivos
            truckCopy.estatus = lastDigit % 2 === 0 ? 'Mantenimiento preventivo' : 'Mantenimiento correctivo';
            
            console.log(`🔄 Mapeando camión ${uniqueId} (último dígito: ${lastDigit}) a estado: ${truckCopy.estatus}`);
          } else {
            // Si no hay dígitos, usar una suma de caracteres para determinar
            let charSum = 0;
            for (let i = 0; i < uniqueId.length; i++) {
              charSum += uniqueId.charCodeAt(i);
            }
            truckCopy.estatus = charSum % 2 === 0 ? 'Mantenimiento preventivo' : 'Mantenimiento correctivo';
            
            console.log(`🔄 Mapeando camión ${uniqueId} (suma de caracteres: ${charSum}) a estado: ${truckCopy.estatus}`);
          }
        } catch (error) {
          console.error('Error en el mapeo de estado:', error);
          // En caso de error, usar el último carácter como antes
          const lastChar = String(uniqueId).slice(-1);
          const lastNum = parseInt(lastChar);
          
          if (!isNaN(lastNum)) {
            truckCopy.estatus = lastNum % 2 === 0 ? 'Mantenimiento preventivo' : 'Mantenimiento correctivo';
          } else {
            // Si no es un número, usar preventivo por defecto
            truckCopy.estatus = 'Mantenimiento preventivo';
          }
        }
      }
    }
    
    // Asegurar compatibilidad: asignar también la propiedad `status` usada por el frontend
  truckCopy.status = truckCopy.estatus;

  // Alias para campos comunes: asegurar que el frontend encuentre valores en las propiedades que espera
  truckCopy.type = truckCopy.type || truckCopy.tipo_unidad || truckCopy.tipoUnidad || truckCopy.tipo_unidad_descripcion || truckCopy.type_description || '';
  truckCopy.plates = truckCopy.plates || truckCopy.placas || truckCopy.plate || truckCopy.placa || '';
  truckCopy.mileage = truckCopy.mileage || truckCopy.km_actual || truckCopy.kmActual || truckCopy.km || 0;
  // Modelo (mapear 'modelo' del backend a 'model' consumido por algunas vistas)
  truckCopy.model = truckCopy.model || truckCopy.modelo || truckCopy.modelName || truckCopy.modelo_unidad || '';
  // nextService puede venir como km_prox_servicio o como fecha; mantener el valor original si existe
  truckCopy.nextService = truckCopy.nextService || truckCopy.km_prox_servicio || truckCopy.km_proximo_servicio || truckCopy.next_service || truckCopy.km_prox || '';
  truckCopy.lastService = truckCopy.lastService || truckCopy.last_service || truckCopy.fecha_ultimo_servicio || truckCopy.lastServiceDate || '';
  // Normalizar número económico
  truckCopy.numero_economico = truckCopy.numero_economico || truckCopy.numeroEconomico || truckCopy.numero || truckCopy.numero_eco || truckCopy.numero_econ || '';
    return truckCopy;
  }
  
  // Mapear estado del frontend al backend
  mapFrontendStatusToBackend(status) {
    // Guardar exactamente lo que el usuario seleccionó para los tipos de mantenimiento
    const statusMapping = {
      'Activo': 'Activo',
      'Mantenimiento preventivo': 'Mantenimiento preventivo',
      'M. Preventivo': 'Mantenimiento preventivo',
      'Mantenimiento correctivo': 'Mantenimiento correctivo',
      'M. Correctivo': 'Mantenimiento correctivo',
      'Baja': 'Baja'
    };

    return statusMapping[status] || status;
  }

  getTipoUnidadOptions() {
    return [
      { value: 'Tractocamion', label: 'Tractocamión' },
      { value: 'DOLLY', label: 'Dolly' },
      { value: 'AUTO TANQUE', label: 'Auto Tanque' },
      { value: 'GRUA', label: 'Grúa' },
      { value: 'SEMI REMOLQUE', label: 'Semi Remolque' },
      { value: 'CAJA SECA', label: 'Caja Seca' },
      { value: 'UNIDAD DE GASOLINA', label: 'Unidad de Gasolina' },
      { value: 'Quinta rueda', label: 'Quinta rueda' },
      { value: 'Camión rígido', label: 'Camión rígido' },
      { value: 'Remolque', label: 'Remolque' },
      { value: 'semi remolque', label: 'Semi remolque' }
    ];
  }
  
  // Función para normalizar el tipo de unidad para comparaciones consistentes
  normalizeTipoUnidad(tipoUnidad) {
    if (!tipoUnidad) return '';
    
    // Eliminar acentos, convertir a minúsculas y eliminar espacios extra
    const normalizar = (texto) => {
      try {
        return String(texto)
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
          .trim();
      } catch (e) {
        return String(texto)
          .toLowerCase()
          .replace(/[áàäâÁÀÄÂ]/g, 'a')
          .replace(/[éèëêÉÈËÊ]/g, 'e')
          .replace(/[íìïîÍÌÏÎ]/g, 'i')
          .replace(/[óòöôÓÒÖÔ]/g, 'o')
          .replace(/[úùüûÚÙÜÛ]/g, 'u')
          .replace(/ñ/g, 'n')
          .trim();
      }
    };

    const tipNormalizado = normalizar(tipoUnidad);

    // Mapeo de variantes comunes a valores estándar (base)
    const mapeoTipos = {
      'tractocamion': 'tractocamion',
      'tracto': 'tractocamion',
      'tractocamión': 'tractocamion',
      'tracto camion': 'tractocamion',
      'tracto camión': 'tractocamion',
      'dolly': 'dolly',
      'autotanque': 'autotanque',
      'auto tanque': 'autotanque',
      'grua': 'grua',
      'grúa': 'grua',
      'gruas': 'grua',
      'semiremolque': 'semiremolque',
      'semi remolque': 'semiremolque',
      'remolque': 'semiremolque',
      'cajaseca': 'cajaseca',
      'caja seca': 'cajaseca',
      'unidadgas': 'unidadgas',
      'unidad de gasolina': 'unidadgas',
      'unidad gasolina': 'unidadgas',
      'unidad gas': 'unidadgas',
      'unidad de gas': 'unidadgas',
      'camioneta': 'camioneta'
    };

    // 1) Intentar coincidencia exacta
    if (mapeoTipos[tipNormalizado]) return mapeoTipos[tipNormalizado];

    // 2) Intentar coincidencia por inclusión de alguna de las claves
    const keys = Object.keys(mapeoTipos);
    for (const k of keys) {
      if (tipNormalizado.includes(k)) return mapeoTipos[k];
    }

    // 3) Si no hay coincidencias, intentar quitar sufijos comunes (modelo, año, etc.)
    const stripped = tipNormalizado.replace(/\b(modelo|modelo\s\w+|\d{4}|\-\s?\d{4})\b/g, '').trim();
    for (const k of keys) {
      if (stripped.includes(k)) return mapeoTipos[k];
    }

    // 4) Devolver la versión normalizada por defecto
    return tipNormalizado;
  }

  // Obtener distribución de marcas de tractocamiones
  async getTractocamionesMarcas() {
    try {
      console.log('📊 Obteniendo distribución de marcas de tractocamiones');
      const response = await api.get('/trucks/tractocamiones/marcas');
      console.log('✅ Marcas obtenidas:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas:', error);
      throw this.handleError(error);
    }
  }

  // Obtener distribución de marcas de dollys
  async getDollysMarcas() {
    try {
      const response = await api.get('/trucks/dollys/marcas');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas de dollys:', error);
      throw this.handleError(error);
    }
  }

  // Obtener distribución de marcas de semi remolques
  async getSemiRemolquesMarcas() {
    try {
      const response = await api.get('/trucks/semiremolques/marcas');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas de semi remolques:', error);
      throw this.handleError(error);
    }
  }

  // Obtener distribución de marcas de auto tanques
  async getAutoTanquesMarcas() {
    try {
      const response = await api.get('/trucks/autotanques/marcas');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas de auto tanques:', error);
      throw this.handleError(error);
    }
  }

  // Obtener distribución de marcas de cajas secas
  async getCajasSecasMarcas() {
    try {
      const response = await api.get('/trucks/cajassecas/marcas');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas de cajas secas:', error);
      throw this.handleError(error);
    }
  }

  // Obtener distribución de marcas de unidades de gasolina
  async getUnidadesGasMarcas() {
    try {
      const response = await api.get('/trucks/unidadesgas/marcas');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas de unidades de gasolina:', error);
      throw this.handleError(error);
    }
  }

  // Obtener distribución de marcas de grúas
  async getGruasMarcas() {
    try {
      const response = await api.get('/trucks/gruas/marcas');
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo marcas de grúas:', error);
      throw this.handleError(error);
    }
  }
}



export const trucksService = new TrucksService();
